<template>
  <div v-if="getError('variant.base')" class="bg-red-50 p-2 rounded-lg text-red-500 mb-3">
    <span class="text-sm">{{ getError('variant.base') }}</span>
  </div>

  <form @submit.prevent="submitForm" @input="dirty = true">
    <div>
      <label for="text" class="text-sm font-medium text-gray-700 flex">
        {{ t('slides_editor.components.question_form.question') }}
      </label>

      <textarea
        v-model="question.text"
        @input="debouncedSubmit"
        class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-black/50"
      />
      <div v-if="getError('text')" class="text-red-500 text-sm">{{ getError('text') }}</div>
    </div>

    <component
      :is="variantComponent(question.variant_name)"
      :question="question"
      @on-input="debouncedSubmit"/>

    <div v-if="errorMessage" class="text-red-500 text-sm">{{ errorMessage }}</div>
  </form>
</template>

<script setup>
import { ref, inject, watch, onMounted, onBeforeUnmount } from 'vue'
import debounce from 'debounce'
import { VariantOpenFields, VariantPollFields, VariantQuizFields } from './'
import {
  useQuestionHelpers,
  useBlockUpdate,
  useSaveStatus,
  useSidePanelHelpers
} from '@/features/slides_editor/composables'
import { t } from '@/libs/i18n'

const VARIANT_TO_COMPONENT = {
  open: VariantOpenFields,
  poll: VariantPollFields,
  quiz: VariantQuizFields
}

const props = defineProps({
  question: { type: Object, required: true },
  block: { type: Object, required: true },
  isNew: { type: Boolean, default: false }
})

const errorMessage = ref('')
const dirty = ref(false)
const initialState = { ...props.question }

const slides = inject('parsedSlides')
const errors = inject('errors')

const { onAfterCreate, syncSavedIds } = useQuestionHelpers()
const { onBlockUpdate } = useBlockUpdate(props.block, { showErrorStatus: false })
const { setErrors, resetErrors } = useSaveStatus()
const { fixSidePanel, unfixSidePanel, setBeforeCloseHook, clearBeforeCloseHook } = useSidePanelHelpers()

const getError = (field) => {
  const errorPath = `section.questions.${field}`
  return errors.value[errorPath]?.[0]
}

const requiresAnswers = (question) => {
  return ['quiz', 'poll'].includes(question.variant_name?.toLowerCase())
}

const validateQuestion = () => {
  const { question } = props
  const newErrors = {}

  if (!question.text?.trim()) {
    newErrors['section.questions.text'] = [t('slides_editor.components.question_form.errors.text_required')]
  }

  if (requiresAnswers(question)) {
    const answers = question.variant_attributes?.answers_attributes || []
    const activeAnswers = answers.filter(a => !a._destroy)

    const filledAnswers = activeAnswers.filter(a => a.text?.trim())
    if (filledAnswers.length < 2) {
      newErrors['section.questions.variant.base'] = [t('slides_editor.components.question_form.errors.answer_text_required')]
    }
  }

  if (Object.keys(newErrors).length > 0) {
    setErrors(newErrors)
    return false
  }

  resetErrors()
  return true
}

onMounted(() => {
  setBeforeCloseHook(validateQuestion)
})

const submitForm = async () => {
  const { section } = props.block
  const { question } = props

  if (requiresAnswers(question)) {
    const answers = question.variant_attributes?.answers_attributes || []
    const activeAnswers = answers.filter(a => !a._destroy)
    const filledAnswers = activeAnswers.filter(a => a.text?.trim())
    if (filledAnswers.length < 2) return
  }

  const sectionParams = {
    id: section.id,
    questions_attributes: section.questions
  }

  const response = await onBlockUpdate(sectionParams, 'section_attributes')
  if (!response) return

  if (question.id) {
    syncSavedIds(section.questions, savedQuestions(response))
  } else {
    onAfterCreate(slides, response)
  }
}

const savedQuestions = (response) => {
  return response.slide.blocks.find(b => b.block_type === 'section')?.section?.questions || []
}

const debouncedSubmit = debounce(submitForm, 1000)
const variantComponent = (variantType) => VARIANT_TO_COMPONENT[variantType.toLowerCase()]

watch(errors, (newErrors) => {
  if (Object.keys(newErrors).length > 0) {
    fixSidePanel()
  } else {
    unfixSidePanel()
  }
}, { deep: true })

onBeforeUnmount(() => {
  clearBeforeCloseHook()

  if (Object.keys(errors.value).length > 0) {
    if (!dirty.value) {
      if (props.isNew) {
        props.block.section.questions.pop()
      } else {
        Object.assign(props.question, initialState)
      }
    }

    resetErrors()
  }
})
</script>

<style>
  textarea {
    field-sizing: content;
  }
</style>
